/** @fileoverview Resolves shared OData row-filter definitions for lists and input options. */

import type {
  FabrikGlobals,
  FabrikODataFilterOperator,
  FabrikODataRowFilter,
  FabrikRecordKeyRefValue,
  FabrikRowValues,
  InputSnapshot,
} from '../../types/apps';
import { hasRequiredODataFieldType } from './globals';
import { odataLiteral } from './odataLiterals';
import { valueForReference } from './valueRefs';

const ODATA_FILTER_OPERATORS: ReadonlySet<FabrikODataFilterOperator> = new Set([
  'eq',
  'ne',
  'gt',
  'ge',
  'lt',
  'le',
  'contains',
]);

const refLabel = (ref: FabrikRecordKeyRefValue) =>
  ref.scope === 'globals'
    ? `globals.${ref.field}`
    : ref.scope === 'inputs'
    ? `${ref.formId ? `forms.${ref.formId}` : 'standalone'}.${ref.field}`
    : `${ref.dataSourceId}.${ref.field}`;

const isBlank = (value: unknown) =>
  value === undefined ||
  value === null ||
  (typeof value === 'string' && !value.trim()) ||
  (Array.isArray(value) && value.length === 0);

/** Resolves configured row filters to OData predicates using the current screen snapshot. */
export const resolveODataRowFilters = (
  rowFilters: FabrikODataRowFilter[] | undefined,
  fields: FabrikRowValues,
  globals: FabrikGlobals,
  inputs: InputSnapshot
): string[] =>
  (rowFilters ?? []).flatMap(
    ({ field, value, operator = 'eq', fieldType, omitWhenEmpty, when }) => {
      if (!ODATA_FILTER_OPERATORS.has(operator))
        throw new Error(`Unsupported row filter operator: ${operator}.`);
      if (when && valueForReference(fields, when.value, globals, inputs) !== when.equals) return [];
      const resolved = valueForReference(fields, value, globals, inputs);
      if (omitWhenEmpty && isBlank(resolved)) return [];
      if (resolved === undefined)
        throw new Error(`Required row data ${refLabel(value)} is unavailable.`);
      if (!hasRequiredODataFieldType(value, fieldType))
        throw new Error('Date/time globals row filters require the target OData field type.');
      if (operator === 'contains') {
        if (fieldType !== undefined && fieldType !== 'Edm.String')
          throw new Error('Text-match filters require an Edm.String field.');
        const textValue =
          typeof resolved === 'string'
            ? resolved
            : typeof resolved === 'number' || typeof resolved === 'boolean'
            ? String(resolved)
            : undefined;
        if (textValue === undefined) throw new Error('Text-match filters require a scalar input.');
        return [`substringof(${odataLiteral(textValue, 'Edm.String')},${field})`];
      }
      return [`${field} ${operator} ${odataLiteral(resolved, fieldType)}`];
    }
  );
